import { MutateNominationPeriodAPIData } from '../../queries/nominations';
import type { RuleCondition } from '../../queries/users';
import { GroupType } from '../../schemas/groups';
import type { UserNominationPeriod } from '../../schemas/nominations';
import { toTimelessDateString } from '../../utils/date';
import { NominationPeriodFormData } from './types';

const toRuleConditions = (conditions: NominationPeriodFormData['nomineeConditions'] | undefined) =>
	(conditions ?? []).filter((c): c is { attr: string; value: string } => !!c.attr && !!c.value).map((c) => ({ attr: c.attr, value: c.value }));

const toSearchRuleConditions = (rows: { attr?: string; value?: string | undefined }[] | undefined): RuleCondition[] =>
	(rows ?? []).flatMap((c) => {
		if (!c.attr || c.value === undefined || c.value === '') {
			return [];
		}
		return [{ attr: c.attr, value: c.value }];
	});

export type NomineeUserSearchPickerParams = {
	conditions: RuleCondition[];
	operator: 'and' | 'or';
	excludeConditions: RuleCondition[];
};

export const nomineeUserSearchParamsFromPeriod = (period: UserNominationPeriod): NomineeUserSearchPickerParams => {
	const group = period.nomineeRulesGroup;
	const conditions = toSearchRuleConditions(group?.conditions);
	const operator: 'and' | 'or' = group?.groupType === GroupType.And ? 'and' : 'or';
	const excludeConditions = toSearchRuleConditions(period.nomineeRuleExcludeConditions ?? []);
	return { conditions, operator, excludeConditions };
};

export const nomineeUserSearchParamsFromPreview = (
	pick: Pick<NominationPeriodFormData, 'nomineeConditions' | 'nomineeConditionsOperator' | 'nomineeExcludeConditions'>
): NomineeUserSearchPickerParams => {
	const conditions = toSearchRuleConditions(pick.nomineeConditions);
	const operator: 'and' | 'or' = pick.nomineeConditionsOperator === GroupType.And ? 'and' : 'or';
	const excludeConditions = toSearchRuleConditions(pick.nomineeExcludeConditions);
	return { conditions, operator, excludeConditions };
};

export const buildFormBody = (data: NominationPeriodFormData): MutateNominationPeriodAPIData => {
	const isGroupMode = data.nomineeType === 'group';

	return {
		name: data.name,
		description: data.description,
		startDate: toTimelessDateString(data.startDate),
		endDate: toTimelessDateString(data.endDate),
		nominationLimit: data.nominationLimit,
		nomineeType: data.nomineeType,
		nomineeGroupType: isGroupMode ? data.nomineeGroupType : null,
		// Group mode replaces audience-based eligibility entirely; the server clears any rules.
		nomineeRuleConditions: isGroupMode ? [] : toRuleConditions(data.nomineeConditions),
		nomineeRuleConditionsOperator: isGroupMode ? undefined : data.nomineeConditionsOperator,
		nominatorRuleConditions: toRuleConditions(data.nominatorConditions),
		nominatorRuleConditionsOperator: data.nominatorConditionsOperator,
		nomineeRuleExcludeConditions: isGroupMode ? [] : toRuleConditions(data.nomineeExcludeConditions),
		nominatorRuleExcludeConditions: toRuleConditions(data.nominatorExcludeConditions),
		questions: data.questions
			.filter((q) => q.questionText.trim().length > 0)
			.map((q) => ({
				...(q.id ? { id: q.id } : {}),
				questionText: q.questionText,
				questionType: q.questionType,
				options: q.questionType === 'text' ? [] : q.options.filter((o) => o.trim().length > 0),
				isRequired: q.isRequired,
			})),
	};
};
